import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Check, Plus } from 'lucide-react'
import type { Connector, ConnectorType } from '@/types'
import {
  CONNECTOR_LABELS,
  CONNECTOR_DESCRIPTIONS,
  CONNECTOR_OVERVIEWS,
  CONNECTOR_HOW_IT_WORKS,
  usesInstanceManagement,
} from './connectorConstants'
import { CONNECTOR_ICONS } from './connectorIcons'
import { ConnectionRow } from './ConnectionRow'
import { DisconnectConfirmDialog } from './DisconnectConfirmDialog'

interface ConnectorDetailViewProps {
  connectorType: ConnectorType | null
  projectId: string
  connections: Connector[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onConnect: (type: ConnectorType) => void
  onDisconnect: (type: ConnectorType) => void
  onAddConnection: (type: ConnectorType) => void
  onDisconnectConnection: (connection: Connector) => Promise<void>
  onReconnectConnection: (connection: Connector) => void
  isDisconnecting: boolean
  isDisconnectingConnection: boolean
  canManage: boolean
}

export function ConnectorDetailView({
  connectorType,
  projectId,
  connections,
  open,
  onOpenChange,
  onConnect,
  onDisconnect,
  onAddConnection,
  onDisconnectConnection,
  onReconnectConnection,
  isDisconnecting,
  isDisconnectingConnection,
  canManage,
}: Readonly<ConnectorDetailViewProps>) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<Connector | null>(null)

  if (!connectorType) return null

  const Icon = CONNECTOR_ICONS[connectorType]
  const label = CONNECTOR_LABELS[connectorType]
  const isConnected = connections.some((c) => c.isEnabled)
  const managesConnections = usesInstanceManagement(connectorType)

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-screen h-screen m-0 rounded-none data-[state=open]:slide-in-from-bottom-0 data-[state=closed]:slide-out-to-bottom-0 left-0 top-0 translate-x-0 translate-y-0 flex flex-col p-0 gap-0 border-0"
      >
        <DialogHeader className="flex-none border-b px-6 py-4">
          <DialogTitle>
            {isConnected ? label : 'Enable Connector'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">

            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted shrink-0">
                <Icon className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-semibold">{label}</h2>
                  {isConnected && !managesConnections && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-success text-success-foreground border-success-foreground" data-testid="connector-connected-status">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {CONNECTOR_DESCRIPTIONS[connectorType]}
                </p>
                {isConnected && managesConnections && (
                  <span
                    className="mt-3 inline-flex items-center gap-1.5 rounded-md border bg-success px-3 py-1.5 text-xs font-medium text-success-foreground border-success-foreground"
                    data-testid="connector-enabled-status"
                  >
                    <Check className="h-4 w-4" />
                    Enabled
                  </span>
                )}
                {canManage && !isConnected && (
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => onConnect(connectorType)}
                      data-testid="enable-connector-button"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Enable
                    </Button>
                  </div>
                )}
                {canManage && isConnected && !managesConnections && (
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmOpen(true)}
                      className="bg-danger text-danger-foreground border-danger-foreground hover:bg-danger hover:text-danger-foreground"
                      data-testid="disconnect-connector-button"
                    >
                      Disconnect
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Overview</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {CONNECTOR_OVERVIEWS[connectorType]}
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">How It Works</h3>
              <ol className="list-decimal list-inside space-y-2">
                {CONNECTOR_HOW_IT_WORKS[connectorType].map((step, index) => (
                  <li key={index} className="text-sm text-muted-foreground leading-relaxed">
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            {managesConnections && connections.length > 0 && (
              <>
                <Separator />
                <section className="space-y-3" data-testid="manage-connections-section">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Manage Connections</h3>
                    {canManage && (
                      <Button
                        size="sm"
                        onClick={() => onAddConnection(connectorType)}
                        data-testid="new-connection-button"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New Connection
                      </Button>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {connections.map((connection) => (
                      <ConnectionRow
                        key={connection.id}
                        projectId={projectId}
                        connection={connection}
                        canManage={canManage}
                        onDisconnect={setConnectionToDisconnect}
                        onReconnect={onReconnectConnection}
                      />
                    ))}
                  </ul>
                </section>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
    <DisconnectConfirmDialog
      projectId={projectId}
      connectorType={connectorType}
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      onConfirm={() => {
        setConfirmOpen(false)
        onDisconnect(connectorType)
      }}
      isDisconnecting={isDisconnecting}
    />
    <DisconnectConfirmDialog
      projectId={projectId}
      connectorType={connectorType}
      connectorId={connectionToDisconnect?.id}
      connectionName={connectionToDisconnect?.connectionName}
      open={connectionToDisconnect !== null}
      onOpenChange={(nextOpen) => { if (!nextOpen) setConnectionToDisconnect(null) }}
      onConfirm={async () => {
        if (!connectionToDisconnect) return
        await onDisconnectConnection(connectionToDisconnect)
        setConnectionToDisconnect(null)
      }}
      isDisconnecting={isDisconnectingConnection}
    />
    </>
  )
}
